// Views Around Town, a loop screen (decided Oct 26; off since Oct 27, the blocks carry the cameras): live feeds only,
// two to a page, from a ranked pool for the time of day (config/cameras.json: day, night, or traffic in the weekday
// commute hours), with the ballpark camera first on Giants home nights. A feed that errors, never plays, or sends a
// blank card is swapped for the next camera in the pool, and remembered as down for a while so the next run skips it.
// Caltrans feeds are HLS (hls.js); the ABC7 and other YouTube feeds are muted embeds.
// The same class runs the cameras features (Oct 27, programming-plan.md Phase C):
//  - the Live Look at :11 and :41: one camera with movement, full frame, picked for right now (cameras.json `look`);
//  - the long block at :26 and :56 (by night :24 and :54, slower): a hero for the daypart full frame while the second
//    feed tunes in out of sight, then a themed lap in pairs that change one side at a time, each incoming feed tuned in
//    on a hidden third tile ahead of its swap, close-ups, and one last full-frame shot of the better feed on screen.
import { registerDisplay, msg } from '../navigation.mjs';
import { registerFeature } from './scheduler.mjs';
import { escapeHtml } from '../utils/string.mjs';
import FeatureDisplay from './feature-display.mjs';
import { data } from './snapshot.mjs';
import { daypart } from './music.mjs';
import { isToday, dayKey } from './format.mjs';
import { getTimes } from '../../vendor/auto/suncalc.js';
import { bigShipDue } from './under-the-gate.mjs';

const { cameras: config, clock, water } = window.RHL;
const { cameras } = config;
const PER_PAGE = 2;
const QUARTER_MS = 15 * 60000;
const END_MARGIN_S = 8;
// the instant the block should end: the next turn of the minute `until` (else the next quarter hour), less a margin;
// before a feature (not a :00/:30 ID) also less the feature's early start and this block's outro
const blockEnd = (now, until) => {
	let target = Math.ceil((now.getTime() + 1000) / QUARTER_MS) * QUARTER_MS;
	if (Number.isInteger(until)) {
		target = Math.ceil((now.getTime() + 1000) / 60000) * 60000;
		const minuteOf = (t) => Number(new Intl.DateTimeFormat('en-US', { timeZone: clock.timeZone, minute: 'numeric' }).format(t));
		while (minuteOf(target) !== until) target += 60000;
	}
	const atId = Number.isInteger(until) ? until % 30 === 0 : true;
	// the feature at `until` starts this early: its own lead (Markets on the minute, Oct 29), else the clock's
	const next = Number.isInteger(until) ? clock.features.find((f) => f.minutes?.includes(until)) : null;
	const lead = (next?.leadSeconds ?? clock.leadSeconds ?? 0) + (atId ? 0 : 5);
	return target - (END_MARGIN_S + (atId ? 0 : lead)) * 1000;
};
const FEED_WAIT_MS = 9000;
const YOUTUBE_WAIT_MS = 20000;
// how long a YouTube player must play, untouched, before its tile is shown: its title, centre button and logo fade
// about 5-6 s after playback starts or resumes (measured Oct 26; 3.5 s was too soon)
const YOUTUBE_SETTLE_MS = 6500;
const DOWN_FOR_MS = 30 * 60000;
// a feed that was playing and stops (frozen picture, a stream that drops to buffering) gets this long to come back
const STALL_MS = 20000;
const WATCH_MS = 5000;
// camera id → when it was last found down
const down = new Map();

// hls.js, loaded once on first use (the build copies it to rhl/hls.min.js)
let hlsLoader = null;
const loadHls = () => {
	hlsLoader ??= new Promise((resolve) => {
		const script = document.createElement('script');
		script.src = 'rhl/hls.min.js';
		script.onload = () => resolve(window.Hls);
		script.onerror = () => resolve(null);
		document.head.append(script);
	});
	return hlsLoader;
};

const localHour = (now) => Number(new Intl.DateTimeFormat('en-US', { timeZone: clock.timeZone, hour: 'numeric', hourCycle: 'h23' }).format(now));

// Giants at home tonight, by the sports source
const giantsHomeTonight = (now) => (data('sports') ?? []).some((g) => g.team === 'Giants' && (g.atHome ?? g.home === 'SF') && isToday(g.t, now));

// the lap for this quarter hour, begun at a different camera each quarter (so the hour's blocks show different views)
const lap = (ids, now) => {
	const ring = ids.filter((id) => !cameras[id]?.spare);
	const at = ring.length ? (Math.floor(now.getTime() / QUARTER_MS) * 5) % ring.length : 0;
	return [...ring.slice(at), ...ring.slice(0, at), ...ids.filter((id) => cameras[id]?.spare)];
};

// today's sunrise and sunset at the Bay (suncalc wants a time on the local day; 8 PM UTC is midday here)
const sunTimes = (now) => {
	const [y, m, d] = dayKey(now).split('-').map(Number);
	return getTimes(new Date(Date.UTC(y, m - 1, d, 20)), water.point.lat, water.point.lon);
};

// a feed with `hours` airs only inside them (the aquarium's cams go dark at 7 p.m.; Oct 27); a `daylight` feed only
// while there's light to see by: from daylightMinutes[0] before sunrise to daylightMinutes[1] after sunset (Oct 27,
// the kelp forest went black after sunset, well before 7; a camera can set its own margin)
const usableAt = (now) => {
	const hour = localHour(now);
	const sun = sunTimes(now);
	const [before, after] = config.daylightMinutes ?? [10, 15];
	// true takes the default margin after sunset; a number is that camera's own (the kelp forest: -30)
	const light = (daylight) => {
		const margin = typeof daylight === 'number' ? daylight : after;
		return now >= sun.sunrise - before * 60000 && now <= sun.sunset.getTime() + margin * 60000;
	};
	return (id) => !!cameras[id] && !!(cameras[id].live || cameras[id].youtube || cameras[id].still)
		&& (!cameras[id].hours || (hour >= cameras[id].hours[0] && hour < cameras[id].hours[1]))
		&& (cameras[id].daylight === undefined || cameras[id].daylight === false || light(cameras[id].daylight));
};
const isDown = (id, now) => down.get(id) > now.getTime() - DOWN_FOR_MS;

// the loop screen's pool for now, best first, without the ones found down lately
const poolFor = (now) => {
	const hour = localHour(now);
	const { name: part, weekend } = daypart(now);
	let pool = config.pools.day;
	if (part === 'night' || part === 'evening') pool = config.pools.night;
	else if (!weekend && config.rush.some(([from, to]) => hour >= from && hour < to)) pool = config.pools.traffic;
	const ids = [...pool];
	if (giantsHomeTonight(now) && !ids.includes(config.ballpark)) ids.unshift(config.ballpark);
	const usable = ids.filter(usableAt(now));
	const fresh = usable.filter((id) => !isDown(id, now));
	// if everything is marked down, try them all again rather than show nothing
	return fresh.length >= PER_PAGE ? fresh : usable;
};

// where the sun is: in the sunrise or sunset hero's window, dark, or day
const sunMode = (now) => {
	const sun = sunTimes(now);
	const mins = (t) => (now - t) / 60000;
	const { heroWindows: w } = config.block;
	if (mins(sun.sunrise) >= w.sunrise[0] && mins(sun.sunrise) <= w.sunrise[1]) return 'sunrise';
	if (mins(sun.sunset) >= w.sunset[0] && mins(sun.sunset) <= w.sunset[1]) return 'sunset';
	return mins(sun.sunrise) < -w.dark || mins(sun.sunset) > w.dark ? 'night' : 'day';
};

// no two of a kind (beaches, bridge decks) side by side: consecutive cameras in a lap share the screen
const kindOf = Object.fromEntries(Object.entries(config.block.kinds ?? {}).flatMap(([kind, ids]) => ids.map((id) => [id, kind])));
const separate = (ids) => {
	const left = [...ids];
	const out = [];
	while (left.length) {
		const lastKind = kindOf[out.at(-1)];
		const i = left.findIndex((id) => !lastKind || kindOf[id] !== lastKind);
		out.push(...left.splice(Math.max(0, i), 1));
	}
	return out;
};

// the long block's cameras: the hero for the daypart, the theme's lap, then the rest of the block pool and the loop's
// pools as spares. `night` forces the night look (the control room).
const blockPlan = (now, { night = false, theme: asked = null } = {}) => {
	const usable = usableAt(now);
	const ok = (id) => usable(id) && !isDown(id, now);
	const mode = night ? 'night' : sunMode(now);
	const dark = mode === 'night';
	const { block } = config;
	const half = Math.floor(now.getTime() / (2 * QUARTER_MS));
	// a show can ask for a theme (the Morning Commute's traffic cams); its own first camera opens it
	const theme = asked ?? (dark ? 'After Dark' : block.dayThemes[half % block.dayThemes.length]);
	// a big ship due under the bridge in the next few minutes opens the block on the Golden Gate approach (Phase E)
	const ship = !asked && block.bigShip && bigShipDue(0, block.bigShip.withinMinutes, now);
	const shipCam = ship && ok(block.bigShip.camera) ? block.bigShip.camera : null;
	const hero = shipCam ?? (asked ? (block.themes[asked] ?? []).find(ok) : (block.heroes[mode] ?? []).find(ok) ?? (block.heroes.day ?? []).find(ok));
	const themed = separate(lap(block.themes[theme] ?? [], now).filter((id) => id !== hero));
	if (dark && giantsHomeTonight(now)) themed.unshift(config.ballpark);
	const rest = [...(block.pools[dark ? 'night' : 'day'] ?? []), ...Object.values(config.pools).flat()];
	const ids = [...new Set([hero, ...themed, ...rest].filter(Boolean))].filter(usable);
	const fresh = ids.filter((id) => !isDown(id, now));
	return { ids: fresh.length >= PER_PAGE ? fresh : ids, theme, mode };
};

// the Live Look's camera: the sunrise or sunset hero in its window, else the next camera with movement in turn
const lookPlan = (now) => {
	const usable = usableAt(now);
	const mode = sunMode(now);
	const heroes = mode === 'sunrise' || mode === 'sunset' ? config.block.heroes[mode] : [];
	const list = (config.look?.cameras ?? []).filter(usable);
	const half = Math.floor(now.getTime() / (2 * QUARTER_MS));
	const turn = list.length ? [...list.slice(half % list.length), ...list.slice(0, half % list.length)] : [];
	const ids = [...new Set([...heroes.filter(usable), ...turn, ...(config.block.pools.day ?? []).filter(usable)])];
	const fresh = ids.filter((id) => !isDown(id, now));
	return { ids: fresh.length ? fresh : ids };
};

const caption = (camera) => `${escapeHtml(camera.label)}${camera.credit ? ` &middot; ${escapeHtml(camera.credit)}` : ''}`;

// a frame that is one flat color, near-black with a little text on it, or two flat shades is a "No video" card, not
// a view
const blankFrame = (video) => {
	try {
		const canvas = document.createElement('canvas');
		canvas.width = 32;
		canvas.height = 24;
		const ctx = canvas.getContext('2d', { willReadFrequently: true });
		ctx.drawImage(video, 0, 0, 32, 24);
		const pixels = ctx.getImageData(0, 0, 32, 24).data;
		const lum = [];
		for (let i = 0; i < pixels.length; i += 4) lum.push(0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2]);
		const mean = lum.reduce((a, b) => a + b, 0) / lum.length;
		const sd = Math.sqrt(lum.reduce((a, b) => a + (b - mean) ** 2, 0) / lum.length);
		if (sd < 3 || (mean < 25 && sd < 7)) return true;
		// Caltrans's "No video" card (Oct 27: TV388, TV792): a dark gray box on black with a line of small text, so not
		// flat. Nearly every pixel falls in two shades far apart (0.99 on both cards; real views 0.17 to 0.37). A dark
		// night view can bunch into two neighboring shades, so the two must be at least two steps apart (the card: three)
		const bins = new Array(32).fill(0);
		lum.forEach((l) => {
			bins[Math.min(31, Math.floor(l / 8))] += 1;
		});
		const [first, second] = bins.map((count, i) => ({ count, i })).sort((a, b) => b.count - a.count);
		return mean < 80 && (first.count + second.count) / lum.length > 0.92 && Math.abs(first.i - second.i) >= 2;
	} catch {
		// a tainted canvas can't be read: give the feed the benefit of the doubt
		return false;
	}
};

// two tiles through `count` slots from `first`, one side at a time: [a, b], [c, b], [c, d], [e, d]... After the first
// pair only one tile tunes in at each step (Oct 27)
const staggered = (count, first) => {
	if (count <= 2) return [[first, first + 1].slice(0, Math.max(1, count))];
	const steps = [[first, first + 1]];
	for (let next = first + 2; next < first + count; next += 1) {
		const [left, right] = steps.at(-1);
		steps.push((next - first) % 2 === 0 ? [next, right] : [left, next]);
	}
	return steps;
};

const tileHtml = (count, layout) => `<div class="cam-grid ${layout}">${'<div class="cam"><div class="cam-view"></div><div class="cam-caption"></div></div>'.repeat(count)}</div>`;

class ViewsAroundTown extends FeatureDisplay {
	constructor(navId, elemId, { block = false } = {}) {
		super(navId, elemId, block ? 'Views Around the Bay' : 'Views Around Town');
		this.block = block;
		this.players = [];
		this.timers = [];
		this.watches = [];
		this.order = [];
		this.slots = new Map();
		this.played = new Set();
		this.tiles = 0;
		this.onMessage = (event) => this.youtubeMessage(event);
	}

	hasData() {
		return (this.block ? blockPlan(new Date()).ids : poolFor(new Date())).length > 0;
	}

	// each page names the tile slots it shows; a slot keeps its camera across pages
	buildPages(now, options = {}) {
		if (!this.block) {
			const pool = poolFor(now);
			const tiles = Math.min(config.tiles ?? 4, pool.length);
			this.tiles = tiles;
			// the pairs change one side at a time, each camera holding about holdSeconds
			const steps = staggered(tiles, 0);
			const each = Math.round((Math.ceil(tiles / PER_PAGE) * config.holdSeconds) / steps.length);
			return steps.map((slots) => ({
				seconds: each,
				title: ['Views', 'Live'],
				live: true,
				stagger: true,
				slots,
				html: `${tileHtml(PER_PAGE, 'two')}<div class="asof">Live &middot; Caltrans &middot; ABC7</div>`,
			}));
		}
		// a block runs up to the clock's next minute (`until`): a station ID at :00 or :30 takes over at once, while a
		// feature at another minute starts its intro early, so the block leaves room for that. The control room asks for
		// a length outright (`seconds`).
		const end = options.seconds ? now.getTime() + options.seconds * 1000 : blockEnd(now, options.until);
		const seconds = Math.max(60, Math.round((end - now.getTime()) / 1000));

		// the Live Look: one camera, full frame, the whole time
		if (options.look) {
			this.nextOrder = lookPlan(now).ids;
			this.tiles = 1;
			this.theme = null;
			return [{
				seconds, title: ['Live', 'Look'], live: true, slots: [0], html: tileHtml(1, 'one'),
			}];
		}

		const {
			heroSeconds = 20, finalSeconds = 20, holdSeconds = 60, nightHoldSeconds = 90, closeSeconds = 20, maxCloseUps = 3, maxCameras = 16,
		} = config.block;
		const { ids, theme } = blockPlan(now, { night: options.night, theme: options.theme });
		this.nextOrder = ids;
		this.theme = theme;
		const hold = options.night || daypart(now).name === 'night' ? nightHoldSeconds : holdSeconds;
		if (ids.length < 2) {
			this.tiles = 1;
			return [{
				seconds, title: ['Views', theme], live: true, slots: [0], html: tileHtml(1, 'one'),
			}];
		}
		// the hero full frame while the second feed tunes in behind it, then pairs that change one side at a time, a step
		// every half hold, then the last full-frame shot
		const room = seconds - heroSeconds - finalSeconds;
		const most = Math.min(maxCameras, ids.length) - 1;
		const k = Math.max(1, Math.min(most, Math.round(room / (hold / 2))));
		const steps = staggered(k + 1, 0);
		this.tiles = k + 1;
		// close-ups (Oct 27): a camera with movement in it (closeUp in cameras.json) goes full frame after its first step,
		// while the other side tunes in its next camera out of sight; never two close-ups running
		let plan = [];
		let closeUps = 0;
		let last = -9;
		steps.forEach((slots, i) => {
			plan.push({ slots });
			const added = i === 0 ? [] : slots.filter((slot, j) => slot !== steps[i - 1][j]);
			const next = steps[i + 1];
			const pick = added.find((slot) => cameras[ids[slot]]?.closeUp);
			if (pick !== undefined && next?.includes(pick) && closeUps < maxCloseUps && i - last >= 2) {
				plan.push({ slots: next, zoom: next.indexOf(pick) });
				closeUps += 1;
				last = i;
			}
		});
		let stepSeconds = Math.floor((room - closeUps * closeSeconds) / steps.length);
		if (closeUps && stepSeconds < 18) {
			plan = plan.filter((step) => step.zoom === undefined);
			closeUps = 0;
			stepSeconds = Math.floor(room / steps.length);
		}
		// the seconds the rounding left over go to the last pair, so the block still lands on its mark
		const spare = room - closeUps * closeSeconds - steps.length * stepSeconds;
		const two = tileHtml(2, 'two');
		// each caption names its source, so the block carries no footer line (it would sit on the bottom captions)
		const pages = [{
			seconds: heroSeconds, title: ['Views', theme], live: true, stagger: true, slots: [0, 1], zoom: 0, force: true, html: two,
		}];
		plan.forEach(({ slots, zoom }) => pages.push({
			seconds: zoom === undefined ? stepSeconds : closeSeconds,
			title: ['Views', zoom === undefined ? theme : 'Close-Up'],
			live: true,
			stagger: true,
			slots,
			zoom,
			html: two,
		}));
		if (spare > 0) pages.at(-1).seconds += spare;
		// the last shot: the better of the two on screen, full frame (picked when it airs)
		pages.push({
			seconds: finalSeconds, title: ['Views', theme], live: true, stagger: true, slots: steps.at(-1), final: true, html: two,
		});
		return pages;
	}

	// coming onto the screen from either side: a fresh pool. Each tile slot (page x 2 + position) has its own camera
	// from the top of the pool, so going backwards shows the same cameras as going forwards (Oct 26)
	showCanvas(navCmd) {
		if (navCmd === msg.command.firstFrame || navCmd === msg.command.lastFrame || !this.order.length) this.newRun();
		super.showCanvas(navCmd);
	}

	newRun() {
		this.order = this.block ? (this.nextOrder ?? blockPlan(new Date()).ids) : poolFor(new Date());
		this.nextOrder = null;
		if (!this.block) this.tiles = Math.min(config.tiles ?? 4, this.order.length);
		this.slots = new Map();
		this.played = new Set();
	}

	// the tiles on screen, in the order they show (a tile tuned in ahead takes its place by CSS order, since moving a
	// YouTube iframe in the page would reload it)
	tilesNow() {
		return [...(this.elem?.querySelectorAll('.main .cam:not(.cam-ahead)') ?? [])]
			.sort((a, b) => Number(a.style.order || 0) - Number(b.style.order || 0));
	}

	async drawCanvas() {
		const page = this.pages[Math.max(0, this.screenIndex)];
		const tiles = this.tilesNow();
		const before = this.shown;
		// one staggered step to the next: only the side whose slot changed tunes in (or comes up already tuned in, from
		// the hidden tile); the other keeps playing
		if (page?.stagger && before?.stagger && before !== page && tiles.length === page.slots.length) {
			this.shown = page;
			this.holdTiles = true;
			try {
				await super.drawCanvas();
			} finally {
				this.holdTiles = false;
			}
			this.render();
			tiles.forEach((tile, i) => {
				if (before.slots[i] !== page.slots[i]) this.swapIn(tile, page.slots[i]);
			});
			this.dropAhead();
			this.zoom(page, this.tilesNow());
			this.planAhead();
			return;
		}
		this.stopLive();
		this.shown = page;
		if (!this.order.length) this.newRun();
		await super.drawCanvas();
		if (page?.live) this.startLive();
		this.tilesNow().forEach((tile, i) => {
			tile.style.order = i;
		});
		if (page?.zoom !== undefined || page?.final) this.zoom(page, this.tilesNow());
		this.planAhead();
	}

	// a slot coming on: the tile tuned in ahead for it, if there is one, else tune in here
	swapIn(tile, slot) {
		const { ahead } = this;
		if (ahead && ahead.slot === slot && ahead.tile.isConnected) {
			this.ahead = null;
			ahead.tile.style.order = tile.style.order;
			ahead.tile.classList.remove('cam-ahead');
			this.clearTile(tile);
			tile.remove();
			return;
		}
		this.fill(tile, slot);
	}

	// tune ahead (Oct 27, Phase C): aheadSeconds before a step that changes one side, the incoming feed starts on a hidden
	// third tile, so TUNING IN never shows. A close-up step needs none (its other side tunes in out of sight).
	planAhead() {
		const i = Math.max(0, this.screenIndex);
		const page = this.pages[i];
		const next = this.pages[i + 1];
		if (!page?.stagger || !next?.stagger || next.zoom !== undefined) return;
		const changes = next.slots.map((slot, j) => (slot !== page.slots[j] ? j : -1)).filter((j) => j >= 0);
		if (changes.length !== 1) return;
		const slot = next.slots[changes[0]];
		const lead = Math.max(0, page.seconds - (config.block.aheadSeconds ?? 12)) * 1000;
		this.timers.push(setTimeout(() => {
			if (this.shown === page) this.prepareAhead(slot);
		}, lead));
	}

	prepareAhead(slot) {
		this.dropAhead();
		const grid = this.elem?.querySelector('.main .cam-grid');
		if (!grid) return;
		const tile = document.createElement('div');
		tile.className = 'cam cam-ahead';
		tile.innerHTML = '<div class="cam-view"></div><div class="cam-caption"></div>';
		grid.append(tile);
		this.ahead = { slot, tile };
		this.fill(tile, slot);
	}

	dropAhead() {
		if (!this.ahead) return;
		const { tile } = this.ahead;
		this.ahead = null;
		this.clearTile(tile);
		tile.remove();
	}

	// stop a tile's feed before it leaves the page
	// eslint-disable-next-line class-methods-use-this
	clearTile(tile) {
		const view = tile.querySelector('.cam-view');
		if (!view) return;
		view.rhlHls?.destroy();
		view.rhlHls = null;
		clearTimeout(view.rhlSettle);
		const video = view.querySelector('video');
		video?.pause();
		video?.removeAttribute('src');
		view.querySelector('iframe')?.remove();
	}

	// one tile full frame, the other kept playing out of sight: a close-up (only for a feed that's showing), the hero
	// (always, with the second feed tuning in behind it), and the last shot (the better of the two)
	zoom(page, tiles) {
		const grid = this.elem.querySelector('.main .cam-grid');
		const at = page.final ? this.bestSide(tiles) : page.zoom;
		const view = tiles[at]?.querySelector('.cam-view');
		const on = at !== undefined && !!view && (page.force || view.classList.contains('playing'));
		grid?.classList.toggle('zoom-0', on && at === 0);
		grid?.classList.toggle('zoom-1', on && at === 1);
		tiles.forEach((tile, i) => tile.classList.toggle('out', on && i !== at));
		if (page.zoom !== undefined && !on) {
			const bottom = this.elem.querySelector('.header .title.dual .bottom');
			if (bottom) bottom.textContent = this.theme ?? 'Around the Bay';
		}
	}

	// the better feed on screen: one that's playing, then one with movement, then the left
	// eslint-disable-next-line class-methods-use-this
	bestSide(tiles) {
		const score = (tile) => {
			const view = tile.querySelector('.cam-view');
			return (view?.classList.contains('playing') ? 2 : 0) + (cameras[view?.dataset.cam]?.closeUp ? 1 : 0);
		};
		return tiles.map((tile, i) => ({ i, s: score(tile) })).sort((a, b) => b.s - a.s || a.i - b.i)[0]?.i ?? 0;
	}

	// while a staggered step keeps its tiles, the page's HTML isn't redrawn
	canRender() {
		return !this.holdTiles && super.canRender();
	}

	// the slot's own camera, or (once it has failed) a spare from below the slots in the pool
	cameraFor(slot) {
		const taken = new Set(this.slots.values());
		const own = this.order[slot];
		if (!this.slots.has(slot) && own && !(down.get(own) > Date.now() - DOWN_FOR_MS) && !taken.has(own)) {
			this.slots.set(slot, own);
			return own;
		}
		if (this.slots.has(slot) && !(down.get(this.slots.get(slot)) > Date.now() - DOWN_FOR_MS)) return this.slots.get(slot);
		const spare = this.order.slice(this.tiles).find((id) => !taken.has(id) && !(down.get(id) > Date.now() - DOWN_FOR_MS));
		if (spare) this.slots.set(slot, spare);
		else this.slots.delete(slot);
		return spare ?? null;
	}

	startLive() {
		const page = this.pages[Math.max(0, this.screenIndex)];
		let { slots } = page ?? {};
		// the wall shows slots whose feeds played this run, Caltrans first (they come up at once; YouTube takes ~10 s)
		if (page?.wall) {
			const good = [...this.slots.entries()].filter(([, id]) => this.played.has(id));
			good.sort(([, a], [, b]) => Number(!!cameras[a].youtube) - Number(!!cameras[b].youtube));
			if (good.length >= slots.length) slots = good.slice(0, slots.length).map(([slot]) => slot);
		}
		this.tilesNow().forEach((tile, i) => this.fill(tile, slots?.[i] ?? i));
	}

	// put the slot's camera in a tile; called again when a feed fails
	fill(tile, slot) {
		const id = this.cameraFor(slot);
		const view = tile.querySelector('.cam-view');
		const cap = tile.querySelector('.cam-caption');
		view.rhlHls?.destroy();
		view.rhlHls = null;
		clearTimeout(view.rhlSettle);
		view.rhlSettle = null;
		view.querySelector('video')?.pause();
		view.innerHTML = '';
		view.className = 'cam-view';
		if (!id) {
			tile.classList.add('empty');
			cap.textContent = '';
			return;
		}
		tile.classList.remove('empty');
		const camera = cameras[id];
		cap.innerHTML = caption(camera);
		const fail = () => {
			if (!view.isConnected || view.dataset.cam !== id) return;
			down.set(id, Date.now());
			this.fill(tile, slot);
		};
		view.dataset.cam = id;
		if (camera.youtube) this.startYoutube(view, camera, fail);
		else if (camera.still) this.startStill(view, camera, fail);
		else this.startHls(view, camera, fail);
	}

	// a camera that is a picture refreshed every so often (Oct 27: SF State's FogCam, the web's oldest, a new frame every
	// 20 s). Each new frame loads out of sight and swaps in whole, so the tile never blanks; a frame that fails to load
	// twice running takes it off like a dead feed. No blank-frame test: the host sends no CORS header, so its pixels
	// can't be read.
	startStill(view, camera, fail) {
		const img = document.createElement('img');
		img.className = 'cam-still';
		img.alt = '';
		img.referrerPolicy = 'no-referrer';
		view.append(img);
		view.insertAdjacentHTML('beforeend', '<span class="live-dot">LIVE</span>');
		const url = () => `${camera.still}${camera.still.includes('?') ? '&' : '?'}t=${Date.now()}`;
		let misses = 0;
		const next = () => {
			const frame = new Image();
			frame.referrerPolicy = 'no-referrer';
			frame.onload = () => {
				misses = 0;
				if (!img.isConnected) return;
				img.src = frame.src;
				view.classList.add('playing');
				this.played.add(view.dataset.cam);
			};
			frame.onerror = () => {
				misses += 1;
				if (misses >= 2 || !view.classList.contains('playing')) fail();
			};
			frame.src = url();
		};
		next();
		const watch = setInterval(() => {
			if (!img.isConnected) {
				clearInterval(watch);
				return;
			}
			next();
		}, (camera.refreshSeconds ?? 20) * 1000);
		this.watches.push(watch);
		this.timers.push(setTimeout(() => {
			if (!view.classList.contains('playing')) fail();
		}, FEED_WAIT_MS));
	}

	async startHls(view, camera, fail) {
		const video = document.createElement('video');
		video.muted = true;
		video.playsInline = true;
		video.crossOrigin = 'anonymous';
		view.append(video);
		view.insertAdjacentHTML('beforeend', '<span class="live-dot">LIVE</span>');
		let checked = false;
		video.addEventListener('playing', () => {
			view.classList.add('playing');
			this.played.add(view.dataset.cam);
			if (checked) return;
			checked = true;
			// look twice, a few seconds apart, so a dark first frame doesn't count against it
			this.timers.push(setTimeout(() => {
				if (blankFrame(video)) {
					this.timers.push(setTimeout(() => {
						if (blankFrame(video)) fail();
					}, 3000));
				}
			}, 2500));
			// then keep watching while it's up (Oct 27: a dead tile stayed on air): a picture frozen for STALL_MS, or
			// Caltrans swapping in its "No video" card mid-stream (two looks in a row), takes the feed off
			let lastTime = -1;
			let frozenSince = Date.now();
			let blanks = 0;
			const watch = setInterval(() => {
				if (!video.isConnected) {
					clearInterval(watch);
					return;
				}
				if (video.currentTime !== lastTime) {
					lastTime = video.currentTime;
					frozenSince = Date.now();
				}
				blanks = blankFrame(video) ? blanks + 1 : 0;
				if (Date.now() - frozenSince > STALL_MS || blanks >= 2) {
					clearInterval(watch);
					fail();
				}
			}, WATCH_MS);
			this.watches.push(watch);
		});
		video.addEventListener('error', fail);
		this.timers.push(setTimeout(() => {
			if (!view.classList.contains('playing')) fail();
		}, FEED_WAIT_MS));
		const Hls = await loadHls();
		if (Hls?.isSupported()) {
			const hls = new Hls({ liveSyncDurationCount: 2, maxBufferLength: 20 });
			hls.on(Hls.Events.ERROR, (event, error) => {
				if (error.fatal) fail();
			});
			hls.loadSource(camera.live);
			hls.attachMedia(video);
			this.players.push(hls);
			view.rhlHls = hls;
		} else if (video.canPlayType('application/vnd.apple.mpegurl')) {
			video.src = camera.live;
		} else {
			fail();
			return;
		}
		video.play().catch(() => {});
	}

	// a muted, chromeless embed; the player reports its state by postMessage once told we're listening. It sends only
	// this page's origin as the referrer, which YouTube needs to play an embed.
	startYoutube(view, camera, fail) {
		const frame = document.createElement('iframe');
		const params = new URLSearchParams({
			autoplay: '1', mute: '1', controls: '0', playsinline: '1', rel: '0', modestbranding: '1', disablekb: '1', iv_load_policy: '3', enablejsapi: '1', origin: window.location.origin,
		});
		frame.src = `https://www.youtube-nocookie.com/embed/${camera.youtube}?${params}`;
		frame.allow = 'autoplay; encrypted-media';
		frame.referrerPolicy = 'origin';
		frame.title = camera.label;
		frame.className = 'cam-yt';
		view.classList.add('yt');
		// a feed with its own overlays (a logo, a clock, a weather bar) shows only `crop`, centred, at any tile size
		if (Array.isArray(camera.crop)) {
			const [x, y, w, h] = camera.crop;
			view.classList.add('crop');
			frame.style.setProperty('--cw', w);
			frame.style.setProperty('--ch', h);
			frame.style.setProperty('--mx', x + w / 2);
			frame.style.setProperty('--my', y + h / 2);
		}
		view.append(frame);
		view.insertAdjacentHTML('beforeend', '<span class="live-dot">LIVE</span>');
		frame.addEventListener('load', () => {
			frame.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: camera.youtube }), 'https://www.youtube-nocookie.com');
		});
		frame.rhlFail = fail;
		this.timers.push(setTimeout(() => {
			if (!view.classList.contains('playing') && !view.classList.contains('settled-once')) fail();
		}, YOUTUBE_WAIT_MS));
		window.addEventListener('message', this.onMessage);
	}

	youtubeMessage(event) {
		if (!/youtube(-nocookie)?\.com$/.test(new URL(event.origin).hostname)) return;
		let message;
		try {
			message = JSON.parse(event.data);
		} catch {
			return;
		}
		const frame = [...this.elem.querySelectorAll('iframe.cam-yt')].find((f) => f.contentWindow === event.source);
		if (!frame) return;
		const view = frame.parentElement;
		const state = message.info?.playerState ?? (message.event === 'onStateChange' ? message.info : undefined);
		if (state === undefined) return;
		// shown only once the player has played for a while; any other state (buffering, paused, cued) brings its
		// overlay back, so the tile goes back to its tuning-in card until it settles again (ABC7's thumbnails carry a play button and a
		// banner, so they can't stand in)
		if (state === 1) {
			if (!view.rhlSettle && !view.classList.contains('playing')) {
				view.rhlSettle = setTimeout(() => {
					view.rhlSettle = null; view.classList.add('playing'); this.played.add(view.dataset.cam);
				}, YOUTUBE_SETTLE_MS);
				this.timers.push(view.rhlSettle);
			}
		} else {
			clearTimeout(view.rhlSettle);
			view.rhlSettle = null;
			if (view.classList.contains('playing')) {
				view.classList.add('settled-once');
				// it played, then dropped out (buffering, paused, cued): back within STALL_MS or off it goes (Oct 27: a
				// dead tile stayed up behind its tuning-in card)
				clearTimeout(view.rhlStall);
				view.rhlStall = setTimeout(() => {
					if (frame.isConnected && !view.classList.contains('playing')) frame.rhlFail?.();
				}, STALL_MS);
				this.timers.push(view.rhlStall);
			}
			view.classList.remove('playing');
		}
		// ended, or an error from the player: the stream is off air
		if (state === 0 || message.event === 'onError') frame.rhlFail?.();
	}

	stopLive() {
		this.shown = null;
		this.ahead = null;
		this.timers.forEach(clearTimeout);
		this.timers = [];
		this.watches.forEach(clearInterval);
		this.watches = [];
		this.players.forEach((hls) => hls.destroy());
		this.players = [];
		window.removeEventListener('message', this.onMessage);
		this.elem?.querySelectorAll('video').forEach((video) => {
			video.pause();
			video.removeAttribute('src');
		});
		this.elem?.querySelectorAll('iframe.cam-yt').forEach((frame) => frame.remove());
	}

	hideCanvas() {
		this.stopLive();
		super.hideCanvas();
	}
}

const display = new ViewsAroundTown(29, 'views-around-town');
display.makeLoopScreen();
registerDisplay(display);

const block = new ViewsAroundTown(31, 'views-block', { block: true });
registerDisplay(block);
registerFeature('views-block', block);
// the Live Look is the same display with one camera (Phase C)
registerFeature('live-look', block);
